import { useAxiosCreate } from '@/backend/utils';
import authHeader from '@/backend/auth/auth-header';

const http = useAxiosCreate('api');

const baseURL = process.env.VUE_APP_BASE_URL;
const isDev = process.env.NODE_ENV === 'development';

// SSE-стрим нельзя через axios/EventSource (нужны POST + Authorization),
// поэтому отдельный fetch-based путь с теми же правилами base-url, что в utils.js.
function streamUrl(path) {
  return (isDev ? '' : (baseURL || '')) + '/api/v2/ai' + path;
}

export function fetchAiStatus() {
  return http.get('ai/status').then(({ data }) => data);
}

export function fetchChats() {
  return http.get('ai/chats').then(({ data }) => data);
}

export function createChat(title = '', profileId = null) {
  return http
    .post('ai/chats', { title, profile_id: profileId || null })
    .then(({ data }) => data);
}

export function deleteChat(chatId) {
  return http.delete(`ai/chats/${chatId}`).then(({ data }) => data);
}

export function fetchMessages(chatId) {
  return http.get(`ai/chats/${chatId}/messages`).then(({ data }) => data);
}

// Активный (pending) run чата — для переживания refresh во время ответа.
export function fetchActiveRun(chatId) {
  return http.get(`ai/chats/${chatId}/active-run`).then(({ data }) => data);
}

// Повторный вопрос: удалить сообщение и все последующие (обрезка ветки).
export function trimAiMessage(messageId) {
  return http.delete(`ai/messages/${messageId}`).then(({ data }) => data);
}

// Загрузка файла юзера в чат (csv/xlsx/parquet, ≤50 МБ): возвращает
// {message_id, block} — сообщение-заглушка с file-блоком создаёт бэкенд.
export function uploadChatFile(chatId, file) {
  const form = new FormData();
  form.append('file', file);
  return http
    .post(`ai/chats/${chatId}/files`, form)
    .then(({ data }) => data);
}

// --- Профили агентов (пресеты) ---

export function fetchProfiles() {
  return http.get('ai/profiles').then(({ data }) => data);
}

export function setChatProfile(chatId, profileId) {
  return http
    .patch(`ai/chats/${chatId}/profile`, { profile_id: profileId })
    .then(({ data }) => data);
}

export function createProfile({ name, description, prompt, toolsets }) {
  return http
    .post('ai/profiles', { name, description, prompt, toolsets })
    .then(({ data }) => data);
}

export function patchProfile(profileId, patch) {
  return http
    .patch(`ai/profiles/${profileId}`, patch)
    .then(({ data }) => data);
}

export function deleteProfile(profileId) {
  return http.delete(`ai/profiles/${profileId}`).then(({ data }) => data);
}

// --- Отчёты (Stage 2) ---

export function saveReport(chatId, { runId, title = '' }) {
  return http
    .post(`ai/chats/${chatId}/reports`, { run_id: runId, title })
    .then(({ data }) => data);
}

export function fetchReports() {
  return http.get('ai/reports').then(({ data }) => data);
}

export function fetchReport(reportId) {
  return http.get(`ai/reports/${reportId}`).then(({ data }) => data);
}

export function patchReport(reportId, { title, shared }) {
  return http
    .patch(`ai/reports/${reportId}`, { title, shared })
    .then(({ data }) => data);
}

export function deleteReport(reportId) {
  return http.delete(`ai/reports/${reportId}`).then(({ data }) => data);
}

// Повтор без LLM: → {blocks, executed, errors, run_id, llm_calls: 0, llm_tokens: 0}
export function repeatReport(reportId) {
  return http.post(`ai/reports/${reportId}/repeat`).then(({ data }) => data);
}

// Обновление выводов: блоки + 1 LLM-вызов для текста.
export function refreshReport(reportId) {
  return http.post(`ai/reports/${reportId}/refresh`).then(({ data }) => data);
}

// --- Настройки (Stage 3) ---

// Все группы: {group: {schema_version, data: masked, has_override}}
export function fetchAiSettings() {
  return http.get('ai/settings').then(({ data }) => data);
}

// patch — только изменённые поля; api_key: '__clear__' = вернуть env-значение.
export function updateAiSetting(group, patch) {
  return http.put(`ai/settings/${group}`, patch).then(({ data }) => data);
}

// Проверка соединения с LLM-провайдером: {ok, model, latency_ms, preview}.
export function testAiProvider() {
  return http.post('ai/settings/provider/test').then(({ data }) => data);
}

// --- Память (Stage 4) ---

// {manager: bool, items: [{id, source, scope, text, pinned, ...}]}
// юзеру вернёт только его личные заметки, менеджеру — все.
export function fetchMemories(params = {}) {
  return http.get('ai/memories', { params }).then(({ data }) => data);
}

export function createMemory({ text, kind, title, tags, pinned }) {
  return http
    .post('ai/memories', { text, kind, title, tags, pinned })
    .then(({ data }) => data);
}

export function patchMemory(memoryId, { text, kind, title, tags, pinned, is_active }) {
  return http
    .patch(`ai/memories/${memoryId}`, { text, kind, title, tags, pinned, is_active })
    .then(({ data }) => data);
}

export function deleteMemory(memoryId) {
  return http.delete(`ai/memories/${memoryId}`).then(({ data }) => data);
}

// Теги с количеством: {items: [{tag, count}]} — для фильтра и автодополнения.
export function fetchMemoryTags(q = '') {
  return http
    .get('ai/memories/tags', { params: q ? { q } : {} })
    .then(({ data }) => data);
}

// Массовое удаление: {deleted: [id], skipped: [{id, reason}]}.
export function bulkDeleteMemories(ids) {
  return http.post('ai/memories/bulk-delete', { ids }).then(({ data }) => data);
}

// Стрим ответа ассистента: onEvent получает распарсенные SSE-события
// {run_start} {token} {tool_start} {tool_end} {block} {error} {done}.
// Возвращает финальное событие done/error либо null при abort.
export async function streamChatAnswer({ chatId, message, signal, onEvent }) {
  const response = await fetch(streamUrl(`/chats/${chatId}/stream`), {
    method: 'POST',
    headers: { ...authHeader(), Accept: 'text/event-stream' },
    body: JSON.stringify({ message }),
    signal,
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      if (data && data.detail) detail = String(data.detail);
    } catch { /* тело не json — оставляем статус */ }
    throw new Error(detail);
  }
  if (!response.body) throw new Error('Стриминг не поддерживается браузером');

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let final = null;

  const consumeChunk = (text) => {
    buffer += text;
    let sep;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          onEvent(event);
          if (event.type === 'error' || event.type === 'done') final = event;
        } catch { /* битый json-кадр — пропускаем */ }
      }
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    consumeChunk(decoder.decode(value, { stream: true }));
  }
  consumeChunk(decoder.decode());
  return final;
}
